import { relations, sql } from "drizzle-orm";
import {
	boolean,
	index,
	integer,
	pgTable,
	primaryKey,
	serial,
	text,
	timestamp,
} from "drizzle-orm/pg-core";
import { workspace } from "../workspace/workspace-schema";

/* ────────  channels  ──────── */
export const channels = pgTable("channels", {
	id: serial("id").primaryKey(),
	workspaceId: serial("workspace_id")
		.notNull()
		.references(() => workspace.id),
	parentId: integer("parent_id").references(() => channels.id), // Self-referencing foreign key for nested channels
	order: integer("order"), // For sorting channels within the same parent/level (null for private channels)
	type: text("type", {
		enum: [
			"doc",
			"message-group",
			"self-dm",
			"dm",
			"board",
			"sheet",
			"meeting",
			"storage",
			"password",
			"automation",
			"project",
		],
	})
		.notNull()
		.default("message-group"),
	visibility: text("visibility").notNull().default("public"), // "public" | "private"
	title: text("title"), // #general, null for 1-to-1 DM
	description: text("description"), // Channel description (nullable)
	tags: text("tags").array(), // Array of tags for filtering (e.g., ["engineering", "frontend", "urgent"])
	createdAt: timestamp("created_at").notNull().defaultNow(),
	archived: boolean("archived").notNull().default(false),
}, (table) => [
	index("idx_channels_parent_id").on(table.parentId),
	index("idx_channels_workspace_parent").on(table.workspaceId, table.parentId),
	index("idx_channels_order").on(table.workspaceId, table.parentId, table.order),
]);

/* ───────  channel_members  ─────── */
export const channelMembers = pgTable(
	"channel_members",
	{
		channelId: serial("channel_id")
			.notNull()
			.references(() => channels.id),
		userId: text("user_id").notNull(),

		role: text("role").notNull().default("member"), // owner / admin / member
		permissions: text("permissions").notNull().default("rw"), // r (read) | rw (read-write)
		isExternal: boolean("is_external").notNull().default(false), // true if user is not a workspace member
		joinedAt: timestamp("joined_at").notNull().defaultNow(),
	},
	(t) => [
		primaryKey({ columns: [t.channelId, t.userId] }),
		index("idx_members_user").on(t.userId),
		index("idx_members_conv").on(t.channelId),
	],
);

export const channelsRelations = relations(channels, ({ one, many }) => ({
	workspace: one(workspace, {
		fields: [channels.workspaceId],
		references: [workspace.id],
	}),
	members: many(channelMembers),
	parent: one(channels, {
		fields: [channels.parentId],
		references: [channels.id],
		relationName: "parent_child",
	}),
	children: many(channels, {
		relationName: "parent_child",
	}),
}));

export const channelMembersRelations = relations(channelMembers, ({ one }) => ({
	channel: one(channels, {
		fields: [channelMembers.channelId],
		references: [channels.id],
	}),
}));

export const channelReads = pgTable(
	"channel_reads",
	{
		channelId: integer("channel_id")
			.notNull()
			.references(() => channels.id, { onDelete: "cascade" }),
		userId: text("user_id").notNull(),
		lastReadMessageId: integer("last_read_message_id"),
		lastReadAt: timestamp("last_read_at").notNull().defaultNow(),
	},
	(t) => [
		primaryKey({ columns: [t.channelId, t.userId] }),
		index("idx_channel_reads_channel").on(t.channelId),
		index("idx_channel_reads_user").on(t.userId),
	],
);
